/**
 * FilmLocationDirective — the shared "where is this film" directive card.
 *
 * One retrieval directive for BOTH storage modes (BLU-233 two-mode film
 * storage), consumed by the press station (scan garment → go get its film)
 * and the film-storage inspect surface:
 *   - pigeon_hole → "GO TO HOLE-07" + the bound ORDER (#1234) — the hole is
 *     order-keyed, so the order IS the operator's confirmation signal.
 *   - shelf / legacy (rack/drawer/folder) → "GO TO SHLF-03" + a design/size
 *     hint — a shelf is bulk/order-blind, so the operator hand-matches one of
 *     several films by design + size.
 *
 * Sibling of BinDirective / StowDirective: big mono slot label, colour PLUS
 * text for the mode (never colour-only — PRODUCTION_DESIGN §13), distinct
 * icons per mode so the split reads from the glyph alone.
 *
 * `action` switches the kicker verb while keeping the mode tone/glyph stable
 * (a hole is always violet, a shelf always sky — a nice glance-stable property
 * across scan → file → retrieve):
 *   - 'retrieve' (default) → "Go to …" (press station + inspect: go get the film)
 *   - 'stow'               → "File in …" (directed put-away after a cut)
 *   - 'stowed'             → "Already filed in …" (idempotent re-scan FYI; rendered
 *                             with a dashed border so it reads as info, not an action)
 */
import { useTranslation } from 'react-i18next';
import { Archive, Inbox } from 'lucide-react';
import { cn } from '@/lib/utils';

export type FilmLocationDirectiveAction = 'retrieve' | 'stow' | 'stowed';

export interface FilmLocationDirectiveProps {
  slotLabel: string;
  /** 'pigeon_hole' | 'shelf' | legacy values ('rack'/'drawer'/'folder'). */
  storageType: string;
  /** Bound order (pigeon holes; denormalized on the slot). */
  orderName?: string | null;
  orderNumber?: string | null;
  /** Shelf hand-match hints (bulk slots hold many identical films). */
  designHint?: string | null;
  sizeHint?: string | null;
  /** Directive intent — see the module doc. Default 'retrieve'. */
  action?: FilmLocationDirectiveAction;
  className?: string;
}

export function FilmLocationDirective({
  slotLabel,
  storageType,
  orderName,
  orderNumber,
  designHint,
  sizeHint,
  action = 'retrieve',
  className,
}: FilmLocationDirectiveProps) {
  const { t } = useTranslation('production');
  const isHole = storageType === 'pigeon_hole';
  const order = orderName || orderNumber || null;

  // Kicker verb by intent; the tone + glyph below stay mode-driven.
  const kicker =
    action === 'stowed'
      ? t('filmDirective.alreadyFiled')
      : action === 'stow'
        ? isHole
          ? t('filmDirective.fileInHole')
          : t('filmDirective.fileInShelf')
        : isHole
          ? t('filmDirective.goToHole')
          : t('filmDirective.goToShelf');

  // Hole = violet (order-bound, exact); shelf/legacy = sky (bulk, hand-match).
  const tone = isHole
    ? {
        box: 'bg-violet-50 dark:bg-violet-950/30 border-violet-300 dark:border-violet-700',
        kicker: 'text-violet-600 dark:text-violet-400',
        label: 'text-violet-700 dark:text-violet-300',
        icon: 'text-violet-600 dark:text-violet-400',
      }
    : {
        box: 'bg-sky-50 dark:bg-sky-950/30 border-sky-300 dark:border-sky-700',
        kicker: 'text-sky-600 dark:text-sky-400',
        label: 'text-sky-700 dark:text-sky-300',
        icon: 'text-sky-600 dark:text-sky-400',
      };
  const Icon = isHole ? Inbox : Archive;

  // Big-number display (design-sprint lock D5): a FILMPH/HOLE label renders
  // its NUMBER huge ("014") with the full label small underneath — operators
  // read the number, the system keeps the full key. Non-numeric labels
  // (SHELF-B2, legacy) render as-is.
  const numMatch = /^(?:FILMPH|HOLE)-(.+)$/i.exec(slotLabel);
  const bigText = numMatch ? numMatch[1] : slotLabel;
  const subText = numMatch ? slotLabel : null;

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-4 p-6 border-2 rounded-xl animate-enter',
        tone.box,
        // 'stowed' is an FYI (re-scan of an already-filed film), not an action —
        // a dashed border signals "info" while the mode colour stays put.
        action === 'stowed' && 'border-dashed',
        className,
      )}
    >
      <Icon className={cn('h-10 w-10 shrink-0', tone.icon)} aria-hidden />
      <div className="text-center min-w-0">
        {/* Mode kicker — text pairs with the colour + glyph (never colour-only). */}
        <p className={cn('text-sm font-semibold uppercase tracking-wider mb-0.5', tone.kicker)}>
          {kicker}
        </p>
        <p className={cn('font-mono font-bold tracking-wide tabular-nums', tone.label, subText ? 'text-7xl leading-none' : 'text-5xl')}>
          {bigText}
        </p>
        {subText && (
          <p className="text-sm font-mono font-semibold tracking-wide text-muted-foreground mt-1">
            {subText}
          </p>
        )}
        {isHole && order && (
          <p className={cn('text-xl font-semibold mt-1 truncate', tone.label)}>
            {t('filmDirective.forOrder', { order })}
          </p>
        )}
        {!isHole && (designHint || sizeHint) && (
          <p className="text-base text-muted-foreground mt-1 truncate">
            {[designHint, sizeHint].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
    </div>
  );
}
